import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Crop, ImagePlus, Trash2 } from 'lucide-react';
import type { MediaAsset } from '@recipes/contracts';
import { Button, Spinner } from '@recipes/ui';
import { ImageEditorDialog } from '@/components/media/image-editor-dialog';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';

export interface ScanPicture {
  assetId: string;
  url: string | null;
}

const READY_TIMEOUT_MS = 60_000;

/** The copy is processed by the worker (EXIF stripped, variants written) before it has a URL. */
export async function waitUntilReady(assetId: string): Promise<MediaAsset> {
  const started = Date.now();
  for (;;) {
    const asset = await api.media.get(assetId);
    if (asset.status === 'READY' || asset.status === 'FAILED') return asset;
    if (Date.now() - started > READY_TIMEOUT_MS) return asset;
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}

/**
 * "Recipe with its picture": the photos of a scanned cookbook page are AI inputs – they are
 * deleted after the retention time and never belong to the recipe. This lets the user keep one
 * of them as the recipe's picture: it is copied into a permanent recipe picture and opened in
 * the crop tool, so the dish on the page can be cut out.
 */
export function ScanPictureSection({
  sourceAssetIds,
  value,
  onChange,
}: {
  sourceAssetIds: string[];
  value: ScanPicture | null;
  onChange: (picture: ScanPicture | null) => void;
}) {
  const { t } = useTranslation();
  const [copying, setCopying] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const sources = useQueries({
    queries: sourceAssetIds.map((id) => ({
      queryKey: ['media', id],
      queryFn: () => api.media.get(id),
      retry: false,
      staleTime: 5 * 60_000,
    })),
  });
  const available = sources.flatMap((q) => (q.data && q.data.status !== 'DELETED' ? [q.data] : []));
  if (!value && available.length === 0) return null;

  const keep = async (source: MediaAsset) => {
    setCopying(source.id);
    try {
      const copy = await waitUntilReady((await api.media.copy(source.id)).id);
      if (copy.status !== 'READY') throw new Error(copy.errorMessage ?? 'not ready');
      onChange({ assetId: copy.id, url: copy.url });
      // Straight to the crop tool: a page shows more than the dish.
      setEditing(true);
    } catch (error) {
      toastError(error, t('ai.scanPicture.failed'));
    } finally {
      setCopying(null);
    }
  };

  const remove = async () => {
    if (!value) return;
    onChange(null);
    await api.media.remove(value.assetId).catch(() => undefined);
  };

  return (
    <section
      className="space-y-3 rounded-lg border p-4"
      aria-labelledby="scan-picture-heading"
      data-testid="scan-picture"
    >
      <div>
        <h2 id="scan-picture-heading" className="text-base font-semibold">
          {t('ai.scanPicture.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('ai.scanPicture.description')}</p>
      </div>

      {value ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="scan-picture-chosen">
          {value.url && (
            <img
              src={value.url}
              alt={t('ai.scanPicture.chosenAlt')}
              className="h-24 w-32 rounded-md border object-cover"
            />
          )}
          <Button type="button" variant="outline" onClick={() => setEditing(true)}>
            <Crop aria-hidden /> {t('ai.scanPicture.crop')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void remove()}>
            <Trash2 aria-hidden /> {t('ai.scanPicture.remove')}
          </Button>
        </div>
      ) : (
        <ul className="flex flex-wrap gap-3">
          {available.map((source, index) => (
            <li key={source.id} className="w-32 space-y-1">
              {source.thumbnailUrl || source.url ? (
                <img
                  src={source.thumbnailUrl ?? source.url ?? undefined}
                  alt={t('ai.scanPicture.photoAlt', { index: index + 1 })}
                  className="h-24 w-32 rounded-md border object-cover"
                />
              ) : (
                <div className="h-24 w-32 rounded-md border bg-muted" aria-hidden />
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={copying !== null}
                onClick={() => void keep(source)}
                data-testid="scan-picture-use"
              >
                {copying === source.id ? (
                  <Spinner label={t('ai.scanPicture.copying')} />
                ) : (
                  <>
                    <ImagePlus aria-hidden /> {t('ai.scanPicture.use')}
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {value?.url && (
        <ImageEditorDialog
          open={editing}
          onOpenChange={setEditing}
          assetId={value.assetId}
          src={value.url}
          altText={null}
          onSaved={(asset) => {
            // The crop is applied by the worker; the file keeps its address, so the browser
            // would show the old picture from its cache – fetch the new one past the cache.
            void waitUntilReady(asset.id).then(async (ready) => {
              let url = ready.url;
              try {
                if (url) {
                  const fresh = await fetch(url, { cache: 'reload' });
                  if (fresh.ok) url = URL.createObjectURL(await fresh.blob());
                }
              } catch {
                // the signed address still works; only the preview may be stale
              }
              onChange({ assetId: ready.id, url });
            });
          }}
        />
      )}
    </section>
  );
}
